import React from 'react';
import useModelHub from './hooks/useModelHub';
import useModelHubStore from './store/useModelHubStore';
import { typeMeta, ownerKey } from './hubMeta';
import HubToolbar from './components/HubToolbar';
import HubModelCard from './components/HubModelCard';
import HubModelDrawer from './components/HubModelDrawer';
import HubSkeletonGrid from './components/HubSkeletonGrid';
import HubEmptyState from './components/HubEmptyState';
import './PublicModelHub.scss';

const PublicModelHub = () => {
  const { filteredModels, owners, loading, downloadingId, totalCount, handleDownload } = useModelHub();
  const {
    selectedModel,
    setSelectedModel,
    clearFilters,
    searchTerm,
    setSearchTerm,
    activeTypes,
    toggleType,
    ownerFilter,
    setOwnerFilter,
  } = useModelHubStore();

  const openModel = (model) => setSelectedModel(model);
  const closeDrawer = () => setSelectedModel(null);
  // Clicking an owner on a card filters to them (toggles back off if already active).
  const selectOwner = (model) => {
    const key = ownerKey(model);
    setOwnerFilter(ownerFilter === key ? '' : key);
  };

  const ownerName = owners.find((o) => o.id === ownerFilter)?.name;
  const hasFilters = !!searchTerm || activeTypes.length > 0 || !!ownerFilter;
  const shown = filteredModels.length;

  return (
    <div className="public-model-hub">
      <header className="hub-hero">
        <div className="hub-hero__text">
          <h1 className="hub-hero__title">
            <i className="fa fa-cubes" aria-hidden /> Model Hub
          </h1>
          <p className="hub-hero__subtitle">
            Discover, explore and download models shared by the community.
          </p>
        </div>
        <div className="hub-hero__count">
          <span className="hub-hero__count-num">{totalCount}</span>
          <span className="hub-hero__count-label">public {totalCount === 1 ? 'model' : 'models'}</span>
        </div>
      </header>

      {!loading && totalCount > 0 && <HubToolbar owners={owners} totalCount={totalCount} />}

      {!loading && totalCount > 0 && (
        <div className="hub-results">
          <span className="hub-results__count">
            Showing <strong>{shown}</strong>
            {shown !== totalCount ? <> of {totalCount}</> : null} {totalCount === 1 ? 'model' : 'models'}
          </span>

          {hasFilters && (
            <div className="hub-results__filters">
              {searchTerm && (
                <button type="button" className="hub-fpill" onClick={() => setSearchTerm('')}>
                  <i className="fa fa-search" aria-hidden /> “{searchTerm}” <i className="fa fa-times" aria-hidden />
                </button>
              )}
              {activeTypes.map((t) => (
                <button key={t} type="button" className="hub-fpill" onClick={() => toggleType(t)}>
                  {typeMeta(t).label} <i className="fa fa-times" aria-hidden />
                </button>
              ))}
              {ownerFilter && (
                <button type="button" className="hub-fpill" onClick={() => setOwnerFilter('')}>
                  <i className="fa fa-user" aria-hidden /> {ownerName || 'Owner'} <i className="fa fa-times" aria-hidden />
                </button>
              )}
              <button type="button" className="hub-results__clear" onClick={clearFilters}>
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <HubSkeletonGrid />
      ) : totalCount === 0 ? (
        <HubEmptyState variant="empty" />
      ) : filteredModels.length === 0 ? (
        <HubEmptyState variant="no-results" onClear={clearFilters} />
      ) : (
        <div className="hub-grid">
          {filteredModels.map((model) => (
            <HubModelCard
              key={model.model_id}
              model={model}
              onOpen={openModel}
              onSelectOwner={selectOwner}
              onQuickDownload={handleDownload}
              downloadingId={downloadingId}
            />
          ))}
        </div>
      )}

      {selectedModel && (
        <HubModelDrawer
          model={selectedModel}
          onClose={closeDrawer}
          onDownload={handleDownload}
          downloadingId={downloadingId}
        />
      )}
    </div>
  );
};

export default PublicModelHub;
